import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import { ProfileIcon, Skeleton, Text } from '@mercai/clever'

import { MERCAI_WAREHOUSES_OWNER_EMAIL, DECIMAL_PLACES_DEFAULT } from '@/constants'
import { formatCurrency } from '@/helpers'
import { ROUTES_ADMIN } from '@/router'
import { useInventoryDetail } from '@/services'

import { TableInventoryTransaction } from './components'
import { addressLine } from './inventory-detail.constants'

import styles from './inventory-detail.module.css'

export const InventoryDetail = () => {
  const { t } = useTranslation('screenInventoryDetail')

  const { uuid } = useParams()

  const { data: inventory, isLoading: isLoadingInventory } =
    useInventoryDetail(uuid)

  if (isLoadingInventory) {
    return (
      <div className={styles.core}>
        <header className={styles.header}>
          <Text variant="text-small" className={styles.breadcrumb}>
            <Link
              to={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.INVENTORY.fullPath()}
            >{`${t('inventoryTitle')}`}</Link>
            {` / `}
          </Text>
          <div className={styles['header-content']}>
            <div>
              <Text variant="title-base" className={styles['product-title']}>
                <Skeleton height="1.5rem" width="18rem" />
              </Text>

              <Skeleton height="1rem" width="30rem" />
            </div>
          </div>
        </header>

        <div className={styles['supplier-details']}>
          <div className={styles['supplier-details-header']}>
            <ProfileIcon />
            <Text variant={'title-medium'}>{t('supplierTitleLabel')}</Text>
          </div>

          <div className={styles['supplier-details-content']}>
            <div>
              <Text>
                <Skeleton height="1rem" width="6rem" />
              </Text>
            </div>
            <div>
              <Text>
                <Skeleton height="1rem" width="6rem" />
              </Text>
            </div>
            <div>
              <Text>
                <Skeleton height="1rem" width="6rem" />
              </Text>
            </div>
            <div>
              <Text>
                <Skeleton height="1rem" width="6rem" />
              </Text>
            </div>
          </div>
        </div>

        <div className={styles['inventory-details']}>
          <div>
            <Text>
              <Skeleton height="1rem" width="6rem" />
            </Text>
          </div>

          <div>
            <Text>
              <Skeleton height="1rem" width="6rem" />
            </Text>
          </div>

          <div>
            <Text>
              <Skeleton height="1rem" width="6rem" />
            </Text>
          </div>
        </div>

        <div>
          <TableInventoryTransaction isLoading inventoryTransactions={[]} />
        </div>
      </div>
    )
  }

  const productName = inventory?.data?.catalogProduct?.attributes?.title || ''

  return (
    <div className={styles.core}>
      <header className={styles.header}>
        <Text variant="text-small" className={styles.breadcrumb}>
          <Link
            to={ROUTES_ADMIN.SUPPLY.ORDER_MANAGEMENT.INVENTORY.fullPath()}
          >{`${t('inventoryTitle')}`}</Link>
          {` / `}
          <span>{productName}</span>
        </Text>
        <div className={styles['header-content']}>
          <div>
            <Text variant="title-base" className={styles['product-title']}>
              {productName}
            </Text>

            <div className={styles['product-description']}>
              <div>
                <Text>
                  {t('productCategoryLabel')}:{' '}
                  {inventory?.data?.catalogProduct?.category?.name ||
                    t('productEmptyLabel')}
                </Text>
              </div>
              <Text>-</Text>
              <div>
                <Text>
                  {t('productDensityLabel')}:{' '}
                  {inventory?.data?.catalogProduct?.attributes?.density
                    ? inventory?.data?.catalogProduct?.attributes?.density +
                    ' g/cm³'
                    : t('productEmptyLabel')}
                </Text>
              </div>
              <Text>-</Text>
              <div>
                <Text>
                  {t('productFluidityLabel')}:{' '}
                  {inventory?.data?.catalogProduct?.attributes?.fluidity
                    ? inventory?.data?.catalogProduct?.attributes?.fluidity +
                    ' g/10min'
                    : t('productEmptyLabel')}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className={styles['supplier-details']}>
        <div className={styles['supplier-details-header']}>
          <ProfileIcon />
          <Text variant={'title-medium'}>{t('supplierTitleLabel')}</Text>
        </div>

        <div className={styles['supplier-details-content']}>
          <div>
            <Text>
              <strong>{t('supplierNameLabel')}</strong>:{' '}
              {inventory?.data?.supplier?.name || t('productEmptyLabel')}
            </Text>
          </div>

          <div>
            <Text>
              <strong>{t('supplierEmailLabel')}</strong>:{' '}
              {inventory?.data?.supplier?.email || t('productEmptyLabel')}
            </Text>
          </div>

          <div>
            <Text>
              <strong>{t('supplierPhoneLabel')}</strong>:{' '}
              {inventory?.data?.supplier?.phone || t('productEmptyLabel')}
            </Text>
          </div>

          <div>
            <Text>
              <strong>{t('supplierWarehouseAddressLabel')}</strong>:{' '}
              {inventory?.data?.supplierWarehouse
                ? addressLine(inventory?.data?.supplierWarehouse)
                : t('productEmptyLabel')}
            </Text>
          </div>
        </div>
      </div>

      <div className={styles['inventory-details']}>
        <div>
          <Text>
            <strong>{t('inventoryDisponibleQuantityLabel')}</strong>:{' '}
            {inventory?.data?.quantity || inventory?.data?.quantity === 0
              ? `${inventory?.data?.quantity} kg`
              : t('productEmptyLabel')}
          </Text>
        </div>

        <div>
          <Text>
            <strong>{t('inventoryCostLabel')}</strong>:{' '}
            {inventory?.data?.cost
              ? `${formatCurrency(inventory?.data?.cost, 'es-MX', inventory?.data?.currency || 'USD', DECIMAL_PLACES_DEFAULT)}`
              : t('productEmptyLabel')}
          </Text>
        </div>

        <div>
          <Text>
            <strong>{t('inventoryDeliveryOptionLabel')}</strong>:{' '}
            {!inventory?.data?.supplier?.email
              ? t('productEmptyLabel')
              : inventory?.data?.supplier?.email ===
                MERCAI_WAREHOUSES_OWNER_EMAIL
                ? t('inventoryDeliveryOptionDeliverToOurWarehouseLabel')
                : t('inventoryDeliveryOptionKeepAtSupplierLabel')}
          </Text>
        </div>
      </div>

      <div>
        <TableInventoryTransaction
          isLoading={isLoadingInventory}
          inventoryTransactions={inventory?.data?.inventoryTransactions}
        />
      </div>
    </div>
  )
}
